import React, { useCallback, useEffect, useRef, useState } from "react"
import { TextField, Box, IconButton, Tooltip, Alert, Paper } from "@mui/material"
import SportsScoreIcon from "@mui/icons-material/SportsScore"
import GradeIcon from "@mui/icons-material/Grade"
import { useApiService } from "../hooks/Api/useApiService"
import Rating from "../Rating/Rating"
import MoveToInboxIcon from "@mui/icons-material/MoveToInbox"
import OutboxIcon from "@mui/icons-material/Outbox"
import SupportIcon from "@mui/icons-material/Support"
import SupportModal from "../Modals/SupportModal/SupportModal"
import ChatModal from "../Modals/ChatModal/ChatModal"
import MessageIcon from "@mui/icons-material/Message"
import styles from "./Order.module.css"
import SendIcon from "@mui/icons-material/Send"
import CheckIcon from "@mui/icons-material/Check"
import Icn from "../UI/SharieIcn/SharieIcn"
import i18n from "i18next"
import useTranslationSharie from "../hooks/Language/useTranslation"
import UserSection from "../Marketplace/UserSection/UserSection"
import SouthIcon from "@mui/icons-material/South"
import useError from "../hooks/Error/useError"
import CancelIcon from "@mui/icons-material/Cancel"

const Order = (props) => {
  const { postData } = useApiService()
  const [shouldFetch, setShouldFetch] = useState(false)
  const [finishedColor, setFinishedColor] = useState("gray")
  const [rating, setRating] = useState(false)
  const [ratingUpdated, setRatingUpdated] = useState(false)
  const [rated, setRated] = useState(false)
  const [textRate, setTextRate] = useState("")
  const supportRef = useRef()
  const chatRef = useRef()
  const [ratedText, setRatedText] = useState(false)
  const [markedFinished, setMarkedFinished] = useState(false)
  const [fromUser, setFromUser] = useState(null)
  const [toUser, setToUser] = useState(null)
  const isOutgoing = props.from === localStorage.getItem("user")
  const isHebrew = i18n.language === "he"
  const t = useTranslationSharie()
  const fetched_users = []
  const { Error: AlertError, error, success } = useError()

  useEffect(() => {
    getUser(props.from).then(setFromUser)
    getUser(props.to).then(setToUser)
  }, [props.from, props.to])

  // Refactored useEffect to handle all the updates for states
  useEffect(() => {
    // Fetch transactions and check for finished state
    postData("transactions/gettransactions", {
      name: props.name,
      from: props.from,
      date_time: props.date_time,
    }).then((data) => {
      data.transactions.forEach((el) => {
        if (
          el.name === props.name &&
          el.from === props.from &&
          el.date_time === props.date_time &&
          el.progress === "finished"
        ) {
          setFinishedColor("#0096FF")
          setMarkedFinished(true) // Mark the transaction as finished
        }
      })
    })

    // Fetch rating status
    postData("sharie/getrating", { name: props.name, from: props.from, to: props.to }).then(
      (data) => {
        setRated(data.rated) // Set if the rating exists
      }
    )

    // Fetch text for the rating if it exists
    postData("sharie/getratingtext", { name: props.name, from: props.from, to: props.to }).then(
      (data) => {
        setRatedText(data.rating?.text) // Set the text if it exists
      }
    )
  }, [props.name, props.from, props.date_time, props.to])

  const openSupportModal = () => {
    if (supportRef.current) {
      supportRef.current.childFunction()
    }
  }

  const handleRefund = () => {
    postData("transactions/refund", {
      name: props.name,
      from: props.from,
      to: props.to,
      date_time: props.date_time,
      refundable: props.refundable,
    })
      .then((data) => {
        setShouldFetch((prev) => !prev)
        if (data.error) {
          error(data.error)
        } else {
          success("Refund successful!")
        }
      })
      .catch((err) => {
        error("Failed to send refund request.")
      })
  }

  const openChatModal = useCallback(() => {
    if (chatRef.current) {
      chatRef.current.childFunction()
    }
  }, [])

  const sendMarkFinished = () => {
    if (!markedFinished) {
      postData("transactions/markfinished", {
        name: props.name,
        from: props.from,
        to: props.to,
        date_time: props.date_time,
      }).then((data) => {
        if (data.error) {
          error(data.error)
        } else {
          setFinishedColor("#0096FF")
          setMarkedFinished(true)

          success("Order marked as finished!")
        }
      })
    }
  }
  const sendRating = () => {
    postData("sharie/addtextrating", {
      name: props.name,
      from: props.from,
      to: props.to,
      text: textRate,
    }).then((data) => {
      if (data.error) {
        error(data.error)
      } else {
        setRated(true)
        setRatedText(true)
        success("Rating sent!")
      }
    })
  }
  const getUser = (user_name) => {
    const existingUser = fetched_users.find((el) => el.user_name === user_name)
    if (existingUser) {
      return Promise.resolve(existingUser) // return a promise for consistent use
    }

    return postData("users/getuser", { user_name }).then((data) => {
      const user = data?.user[0]
      if (user) {
        fetched_users.push(user)
      }
      return user
    })
  }

  return (
    <>
      {/* Modals */}
      <SupportModal ref={supportRef} skill_name={props.name} />
      <ChatModal ref={chatRef} skill={{ name: props.name, user_name: props.to }} />
      {/* Wrapper for the Paper to enable absolute positioning */}
      <Box sx={{ position: "relative", borderRadius: "0.8rem" }}>
        {/* Conditionally Render CheckIcon */}
        {rated && ratedText && markedFinished && (
          <CheckIcon
            color='success'
            sx={
              isHebrew
                ? {
                    position: "absolute", // Position the icon on top of the Paper
                    top: 10, // Adjust top positioning (space from the top of the Paper)
                    left: 10, // Adjust right positioning (space from the right side of the Paper)
                    zIndex: 1, // Ensure it's above the Paper content
                  }
                : {
                    position: "absolute", // Position the icon on top of the Paper
                    top: 10, // Adjust top positioning (space from the top of the Paper)
                    right: 10, // Adjust right positioning (space from the right side of the Paper)
                    zIndex: 1, // Ensure it's above the Paper content
                  }
            }
          />
        )}

        {/* Main Paper */}
        <Paper
          className={styles.order_container}
          elevation={3}
          sx={{
            backgroundColor: isOutgoing ? "#FFF3E0" : "#E8F5E9",
            borderLeft: isOutgoing ? "5px solid #E65100" : "5px solid #2E7D32",
            padding: 2,
            marginBottom: 2,
            borderRadius: "0.8rem",
          }}
        >
          {/* Order Type Icons */}
          <Box display='flex' justifyContent='space-between' alignItems='center'>
            <Icn
              tooltip={isOutgoing ? "Outgoing Order" : "Incoming Order"}
              color='green'
              label='order-type'
              icon={
                isOutgoing ? (
                  <OutboxIcon sx={{ color: "#E65100" }} />
                ) : (
                  <MoveToInboxIcon sx={{ color: "#2E7D32" }} />
                )
              }
            />
          </Box>
          {/* Order Information */}
          <TextField value={props.name} label={t("Skill Name")} fullWidth margin='dense' />
          {/* <TextField value={props.from || ""} label={t("From")} fullWidth margin='dense' />
          <TextField value={props.to || ""} label={t("To")} fullWidth margin='dense' /> */}
          <UserSection user={fromUser} />
          <SouthIcon
            sx={{ marginInlineStart: "1.5rem", marginBlockStart: "1rem" }}
            color='secondary'
          />
          <UserSection user={toUser} />
          <TextField
            value={new Date(props.date_time).toLocaleString()}
            label={t("Time")}
            fullWidth
            margin='dense'
          />
          {/* Actions */}
          <Box display='flex' justifyContent='space-between' alignItems='center' mt={2}>
            <Icn
              tooltip='Support'
              onClick={openSupportModal}
              label='support'
              icon={<SupportIcon color='primary' />}
            />
            <Icn
              tooltip='Chat'
              onClick={openChatModal}
              label='chat'
              icon={<MessageIcon color='primary' />}
            />

            {/* Rating Actions */}
            {props.to !== localStorage.getItem("user") ? (
              <>
                <Box display='flex' flexDirection='column' alignItems='center'>
                  {/* Conditionally render the Rating component */}
                  {rating && !ratingUpdated && (
                    <Box>
                      <Rating
                        setRatingUpdated={setRatingUpdated}
                        name={props.name}
                        from={props.from}
                        to={props.to}
                        date={props.date}
                      />
                    </Box>
                  )}

                  {/* Icon */}
                  <Icn
                    disabled={rated}
                    tooltip='Rate'
                    onClick={() => setRating(!rating)}
                    label='rate'
                    icon={<GradeIcon sx={{ color: rated ? "#FFD700" : "#BDBDBD" }} />}
                  />
                </Box>

                {/* Mark as Finished */}
                <Tooltip arrow title={t("Mark Finished")}>
                  <IconButton onClick={sendMarkFinished} sx={{ color: finishedColor }}>
                    <SportsScoreIcon />
                  </IconButton>
                </Tooltip>

                {/* Show Rating Input if Rating is Pending */}
                {rated && !ratedText && (
                  <>
                    <TextField
                      defaultValue={textRate}
                      onChange={(e) => setTextRate(e.target.value)}
                      label='Rate'
                      fullWidth
                      margin='dense'
                    />
                    <Icn
                      tooltip='Send Rating'
                      onClick={sendRating}
                      label='send rating'
                      icon={<SendIcon color='primary' />}
                    />
                  </>
                )}
              </>
            ) : (
              props.refundable && (
                <Icn
                  tooltip='Refund'
                  onClick={handleRefund}
                  label='refund skill'
                  icon={<CancelIcon color='error' />}
                />
              )
            )}
          </Box>
        </Paper>
      </Box>
      {/* Error Alert */}
      <AlertError />
    </>
  )
}

export default React.memo(Order)
